import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'

import { registerAsync, onRegisterAttemptFail } from '../store/actions'
import { getInviteContext, InviteContext } from '../api-client/settings-client'
import Input from '../components/input'
import Button from '../components/button'
import UplifterSvg from '../svgs/uplifter'
import styles from '../component-styles/register.module.scss'
import DisplayError from '../components/display-error'
import GoogleButton from '../components/login-button-google'
import MicrosoftButton from '../components/login-button-microsoft'
import { useQuery } from '../helpers'
import Message from '../components/login-message'
import Footer from '../components/footer'

export default function Register({
  history,
}: {
  history: any
}): React.ReactElement {
  const dispatch = useDispatch()
  const { onFail, success } = useSelector((s: any): any => s.register)
  const { login } = useSelector((state: any): any => state)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [token, setToken] = useState('')
  const [terms, setTerms] = useState(false)
  const [offers, setOffers] = useState(false)
  const [inviteContext, setInviteContext] = useState<null | InviteContext>(null)
  const [error, setError] = useState('')

  const query = useQuery()
  const nextToken = query?.get('token')

  useEffect(() => {
    async function fetchContext() {
      if (nextToken) {
        const result = await getInviteContext({ token: nextToken })
        if (result) {
          setInviteContext(result.res?.data?.inviteContext || null)
        } else {
          setError(
            'This invitation link has expired. Please contact your administrator to send you a new link',
          )
        }
      }
    }
    fetchContext()
  }, [])

  useEffect((): void => {
    if (!nextToken) {
      history.push('/login')
    } else {
      setToken(nextToken)
    }
    // eslint-disable-next-line
  }, [nextToken])

  useEffect((): void => {
    if (success) {
      history.push('/welcome')
    }
  }, [history, success])

  const { inviterEmail, companyName, inviteeEmail } = inviteContext || {
    inviterName: '',
    inviterEmail: '',
    inviteeEmail: '',
    companyName: '',
    accountName: '',
  }

  const state = useMemo(() => {
    return {
      fName: firstName,
      lName: lastName,
      password,
      email: inviteeEmail,
      token,
      acceptedMarketing: offers,
    }
  }, [firstName, lastName, password, token, offers, inviteeEmail])

  if (error) {
    return (
      <div className={styles.ssoWrapperOuter}>
        <section className={styles.ssoWrapper}>
          <form
            onSubmit={(e: React.SyntheticEvent): void => e.preventDefault()}
          >
            <UplifterSvg className={styles.svg} />
            <h2>Join Uplifter</h2>
            {inviteContext && (
              <p>
                You have been invited by <strong>{inviterEmail}</strong> to join
                the <strong>{companyName}</strong> account.
              </p>
            )}
            <Message message={error} />
          </form>
        </section>
        <Footer />
      </div>
    )
  }

  return (
    <div className={styles.ssoWrapperOuter}>
      <section className={styles.ssoWrapper}>
        <form onSubmit={(e: React.SyntheticEvent): void => e.preventDefault()}>
          <UplifterSvg className={styles.svg} />
          <h2>Join Uplifter</h2>
          {inviteContext && (
            <p>
              You have been invited by <strong>{inviterEmail}</strong> to join
              the <strong>{companyName}</strong> account.
            </p>
          )}
          {error && <Message message={error} />}
          <div className={styles.divider} />
          <DisplayError attempts={onFail.attempts}>
            {onFail.message}
          </DisplayError>
          <Input
            type="text"
            name="first name"
            label="First Name"
            onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
              setFirstName(event.target.value)
            }
            value={firstName}
            required
          />
          <Input
            type="text"
            name="last name"
            label="Last Name"
            onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
              setLastName(event.target.value)
            }
            value={lastName}
            required
          />
          <Input
            name="offers"
            type="checkbox"
            checked={offers}
            onChange={(): void => setOffers((t: boolean): boolean => !t)}
            label={
              <>
                Receive Uplifter{' '}
                <a
                  href="/privacy-statement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  updates and offers
                </a>
              </>
            }
            required
          />
          <Input
            name="terms"
            type="checkbox"
            checked={terms}
            onChange={(): void => setTerms((t: boolean): boolean => !t)}
            label={
              <>
                I accept the{' '}
                <a
                  href="/terms-of-service.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/privacy-statement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </>
            }
            required
          />
          <div
            className={classNames({
              [styles.inactive]: !!(
                firstName === '' ||
                lastName === '' ||
                !terms
              ),
            })}
          >
            <div className={styles.divider} />
            <h3>Sign up with</h3>
            <GoogleButton
              login={{ ...login, email: inviteeEmail }}
              state={state}
              action="register"
            />
            <MicrosoftButton
              login={{ ...login, email: inviteeEmail }}
              state={state}
              action="register"
            />

            <div className={styles.divider} />
            <h3>Or sign up with password</h3>
            <Input
              type="password"
              name="password"
              label="Password"
              autoComplete="new-password"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
                setPassword(event.target.value)
              }
              value={password}
              required
            />
            <Input
              type="password"
              name="password-confirmation"
              label="Password Confirmation"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
                setPasswordConfirmation(event.target.value)
              }
              value={passwordConfirmation}
              required
            />

            <Button
              submit
              onClick={(): void => {
                if (!firstName) {
                  dispatch(
                    onRegisterAttemptFail('First name field cannot be empty'),
                  )
                } else if (!lastName) {
                  dispatch(
                    onRegisterAttemptFail('Last name field cannot be empty'),
                  )
                } else if (!password) {
                  dispatch(onRegisterAttemptFail('Password cannot be empty'))
                } else if (!passwordConfirmation) {
                  dispatch(
                    onRegisterAttemptFail('Please confirm your password'),
                  )
                } else if (passwordConfirmation !== password) {
                  dispatch(onRegisterAttemptFail('Passwords do not match'))
                } else if (!terms) {
                  dispatch(
                    onRegisterAttemptFail(
                      'Please accept the terms of service and privacy policy before continuing',
                    ),
                  )
                } else {
                  dispatch(registerAsync(state))
                }
              }}
            >
              Sign up
            </Button>
            <Link to="/forgot-password" className={styles.link}>
              Forgot password?
            </Link>
            <Link to="/login" className={styles.link}>
              Already have an account? Login here
            </Link>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  )
}
